/**
 * Portal areas and staff role presets. Mirrors admin_all_areas() and
 * admin_role_default_areas() in 20261008080000_admin_staff_roles.sql; the
 * database enforces access, this file only decides what the portal shows.
 */

export type AdminArea =
  | 'drivers'
  | 'rides'
  | 'riders'
  | 'corridors'
  | 'island_info'
  | 'sponsors'
  | 'reports'
  | 'revenue'
  | 'audit';

/** managersOnly areas are never given to staff: they belong to the owner and directors. */
export const ADMIN_AREAS: { key: AdminArea; label: string; pages: string; managersOnly?: boolean }[] = [
  { key: 'drivers', label: 'Drivers', pages: 'Drivers, Driver Verification' },
  { key: 'rides', label: 'Live operations', pages: 'Live Map, Ride History, Charter, Concierge, Packages' },
  { key: 'riders', label: 'Passengers and safety', pages: 'Passengers, Trust & Safety' },
  { key: 'corridors', label: 'Corridors and fares', pages: 'Corridors, stops, fares' },
  { key: 'island_info', label: 'Island updates', pages: 'Road Hazards, Road Status, Island Alerts, Utility Outages' },
  { key: 'sponsors', label: 'Sponsors', pages: 'Sponsors, Festivals, Explore, Listings' },
  { key: 'reports', label: 'Reports', pages: 'Dashboard, Reports, Filter Analytics' },
  { key: 'revenue', label: 'Revenue', pages: 'Revenue, invoices, payments', managersOnly: true },
  { key: 'audit', label: 'Audit log', pages: 'Audit Log, full team details', managersOnly: true },
];

export const STAFF_AREAS = ADMIN_AREAS.filter((a) => !a.managersOnly);

export const STAFF_ROLES: { key: string; label: string; summary: string; areas: AdminArea[] }[] = [
  {
    key: 'operations',
    label: 'Operations',
    summary: 'Runs the buses day to day: drivers, live rides, corridors and island updates.',
    areas: ['drivers', 'rides', 'corridors', 'island_info'],
  },
  {
    key: 'support',
    label: 'Support',
    summary: 'Helps riders: passenger accounts, safety reports and ride history.',
    areas: ['riders', 'rides'],
  },
  {
    key: 'sales',
    label: 'Sales',
    summary: 'Sells and runs sponsor campaigns, festivals and listings.',
    areas: ['sponsors', 'reports'],
  },
  {
    key: 'viewer',
    label: 'Reports only',
    summary: 'Can read the reports and dashboards, nothing else.',
    areas: ['reports'],
  },
];

export const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  director: 'Director',
  ...Object.fromEntries(STAFF_ROLES.map((r) => [r.key, r.label])),
};

export const MANAGER_ROLES = new Set(['owner', 'director']);

/** Area each portal route belongs to; null means everyone on the team. */
const ROUTE_AREAS: [prefix: string, area: AdminArea | null][] = [
  ['/admin/settings', null],
  ['/admin/requests', null],
  ['/admin/team', null],
  ['/admin/sponsors', 'sponsors'],
  ['/admin/festivals', 'sponsors'],
  ['/admin/explore', 'sponsors'],
  ['/admin/listings', 'sponsors'],
  ['/admin/dashboard', 'reports'],
  ['/admin/reports', 'reports'],
  ['/admin/filter-analytics', 'reports'],
  ['/admin/revenue', 'revenue'],
  ['/admin/live-map', 'rides'],
  ['/admin/ride-history', 'rides'],
  ['/admin/charter', 'rides'],
  ['/admin/concierge', 'rides'],
  ['/admin/packages', 'rides'],
  ['/admin/corridors', 'corridors'],
  ['/admin/drivers', 'drivers'],
  ['/admin/verification', 'drivers'],
  ['/admin/passengers', 'riders'],
  ['/admin/incidents', 'riders'],
  ['/admin/hazards', 'island_info'],
  ['/admin/road-status', 'island_info'],
  ['/admin/alerts', 'island_info'],
  ['/admin/outages', 'island_info'],
  ['/admin/audit', 'audit'],
];

/** Area needed for a portal path; null for pages every team member can open. */
export function areaForPath(path: string): AdminArea | null {
  const match = ROUTE_AREAS.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`));
  return match ? match[1] : null;
}
